import React, { FC, useEffect, useMemo } from "react";
import {
  Button,
  DatePicker,
  Input,
  Select,
  type StatusOption,
} from "@pd/pd-ui-kit";
import { Controller, useForm, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";

import { useGetDealersForSelect } from "~/entity/Dealers";
import { useGetPositionsRestForSelect } from "~/entity/Employee";
import { useGetPartnersForSelect } from "~/entity/Partners/hooks/useGetPartnersForSelect";
import {
  useURLParamsGetter,
  useURLParamsSetter,
} from "~/hooks/useURLParamsSetter";
import {
  clearBaseStaffFilterUrlParams,
  IBaseStaffFilterForm,
  IBaseStaffUrlParams,
  parseBaseStaffFilterFromUrl,
  splitEmployeeFullName,
} from "~/pages/BaseStaff/model/baseStaffUrlParams";
import {
  isGeoFilterBy,
  useBaseStaffGeoFilterOptions,
} from "~/pages/BaseStaff/model/useBaseStaffGeoFilterOptions";
import { DISABLED_FUTURE_DATE } from "~/shared/lib/disabledFutureDate";
import {
  filterOptionByLabel,
  joinCsv,
  toDateString,
} from "~/shared/lib/filterHelpers";

import s from "./BaseStaffFilter.less";

const STATUS_KEYS = [
  "",
  "Unverified",
  "Verified",
  "TemporaryUnavailable",
  "Fired",
  "Archive",
  "SendCheck",
  "Rejected",
] as const;

const EMAIL_VERIFICATION_OPTIONS = [
  { value: "", labelKey: "nothingSelected" },
  { value: "true", labelKey: "verifiedMail" },
  { value: "false", labelKey: "unverifiedMail" },
] as const;

const BaseStaffFilter: FC = () => {
  const { t } = useTranslation();
  const { search } = useLocation();
  const setParams = useURLParamsSetter();
  const urlParams = useURLParamsGetter<IBaseStaffUrlParams>();

  const {
    options: dealershipsForSelect,
    onSearch: searchDealer,
    onScrollBottom: onScrollBottomDealers,
    loading: dealersLoading,
  } = useGetDealersForSelect(true, { pageSize: 100, immediate: true });

  const {
    options: positionOptions,
    loading: positionsLoading,
    onSearch: searchPositions,
    onOpenChange: handlePositionsOpenChange,
  } = useGetPositionsRestForSelect();

  const { control, handleSubmit, reset, setValue } =
    useForm<IBaseStaffFilterForm>({
      defaultValues: {
        fullname: undefined,
        status: undefined,
        email: undefined,
        emailVerification: null,
        workPhone: undefined,
        id: undefined,
        ladaAcademiaId: undefined,
        positionIds: [],
        filterBy: undefined,
        filterValues: [],
        creationFrom: null,
        creationTo: null,
        lastEditFrom: null,
        lastEditTo: null,
      },
    });

  const filterByValue = useWatch({ control, name: "filterBy" }) as
    | string
    | undefined;

  const {
    options: partnersForSelect,
    loading: partnersLoading,
    onSearch: searchPartner,
    onOpenChange: handlePartnersOpenChange,
  } = useGetPartnersForSelect(filterByValue === "partner");

  const { options: geoFilterOptions, loading: geoOptionsLoading } =
    useBaseStaffGeoFilterOptions(filterByValue);

  const isDealerFilter = filterByValue === "dealer";
  const isPartnerFilter = filterByValue === "partner";
  const isGeoFilter = isGeoFilterBy(filterByValue);

  const filterValueOptions = isDealerFilter
    ? dealershipsForSelect
    : isPartnerFilter
      ? partnersForSelect
      : geoFilterOptions;
  const filterValueLoading = isDealerFilter
    ? dealersLoading
    : isPartnerFilter
      ? partnersLoading
      : geoOptionsLoading;

  const statusOptions: StatusOption[] = useMemo(
    () =>
      STATUS_KEYS.map((statusKey) => ({
        value: statusKey,
        label: statusKey
          ? t(`employee.${statusKey}`)
          : t("employee.nothingSelected"),
      })),
    [t],
  );

  const emailVerificationOptions: StatusOption[] = useMemo(
    () =>
      EMAIL_VERIFICATION_OPTIONS.map((item) => ({
        value: item.value,
        label: t(`employee.${item.labelKey}`),
      })),
    [t],
  );

  const filterByOptions: StatusOption[] = useMemo(
    () => [
      { value: "", label: t("employee.nothingSelected") },
      { value: "dealer", label: t("employee.filterByDealer") },
      // TODO PASDILER-2428: временно ограничено до завершения работ в feat/PASDILER-1829. https://task.sotl.group:8443/browse/PASDILER-2428
      // { value: "partner", label: t("employee.partner") },
      { value: "city", label: t("employee.filterByCity") },
      { value: "region", label: t("employee.filterByRegion") },
      { value: "country", label: t("employee.filterByCountry") },
      {
        value: "federalDistrict",
        label: t("employee.filterByFederalDistrict"),
      },
    ],
    [t],
  );

  useEffect(() => {
    reset(
      parseBaseStaffFilterFromUrl(
        urlParams,
        dealershipsForSelect,
        positionOptions,
        partnersForSelect,
      ),
    );
  }, [search, reset, urlParams]);

  const onSubmit = (data: IBaseStaffFilterForm) => {
    const fullName = splitEmployeeFullName(data.fullname);
    const positionIds = data.positionIds.map((item) => item.value);
    const filterValues = data.filterValues.map((item) => item.value);
    const emailVerificationValue =
      data.emailVerification?.value === true
        ? "true"
        : data.emailVerification?.value === false
          ? "false"
          : undefined;

    setParams({
      ...clearBaseStaffFilterUrlParams(),
      id: data.id || undefined,
      surname: fullName.surname,
      name: fullName.name,
      middleName: fullName.middleName,
      status: data.status || undefined,
      email: data.email || undefined,
      workPhone: data.workPhone || undefined,
      emailVerification: emailVerificationValue,
      ladaAcademiaId: data.ladaAcademiaId || undefined,
      positionIds: joinCsv(positionIds),
      filterBy: data.filterBy || undefined,
      filterValues: data.filterBy ? joinCsv(filterValues) : undefined,
      creationFrom: toDateString(data.creationFrom),
      creationTo: toDateString(data.creationTo),
      lastEditFrom: toDateString(data.lastEditFrom),
      lastEditTo: toDateString(data.lastEditTo),
      page: "0",
    });
  };

  const cancelForm = () => {
    reset({
      id: undefined,
      fullname: undefined,
      status: undefined,
      email: undefined,
      workPhone: undefined,
      emailVerification: null,
      ladaAcademiaId: undefined,
      positionIds: [],
      filterBy: undefined,
      filterValues: [],
      creationFrom: null,
      creationTo: null,
      lastEditFrom: null,
      lastEditTo: null,
    });
    setParams({
      ...clearBaseStaffFilterUrlParams(),
      page: "0",
    });
  };

  return (
    <form className={s.formFilter} onSubmit={handleSubmit(onSubmit)}>
      <div className={s.row}>
        <div className={s.field}>
          <span className={s.label}>{t("employee.fullname")}</span>
          <Controller
            name="fullname"
            control={control}
            render={({ onChange, value, onBlur }) => (
              <Input
                value={value || ""}
                onChange={(event) => onChange(event.target.value)}
                onBlur={onBlur}
                placeholder={t("partners.filter.inputPlaceholder")}
              />
            )}
          />
        </div>
        <div className={s.field}>
          <span className={s.label}>{t("employee.statusEmployee")}</span>
          <Controller
            name="status"
            control={control}
            render={({ onChange, value }) => (
              <Select
                labelInValue
                value={
                  statusOptions.find((item) => item.value === (value || "")) ||
                  statusOptions[0]
                }
                options={statusOptions}
                onChange={(option) =>
                  onChange(option?.value ? option.value : undefined)
                }
              />
            )}
          />
        </div>
        <div className={s.field}>
          <span className={s.label}>{t("employee.email")}</span>
          <Controller
            name="email"
            control={control}
            render={({ onChange, value, onBlur }) => (
              <Input
                value={value || ""}
                onChange={(event) => onChange(event.target.value)}
                onBlur={onBlur}
                placeholder={t("partners.filter.inputPlaceholder")}
              />
            )}
          />
        </div>
        <div className={s.field}>
          <span className={s.label}>{t("employee.mainEmailStatus")}</span>
          <Controller
            name="emailVerification"
            control={control}
            render={({ onChange, value }) => {
              const currentValue =
                value?.value === true
                  ? "true"
                  : value?.value === false
                    ? "false"
                    : "";

              return (
                <Select
                  labelInValue
                  value={
                    emailVerificationOptions.find(
                      (item) => item.value === currentValue,
                    ) || emailVerificationOptions[0]
                  }
                  options={emailVerificationOptions}
                  placeholder={t("employee.nothingSelected")}
                  onChange={(option) => {
                    if (!option?.value) {
                      onChange(null);
                      return;
                    }
                    onChange({
                      value: option.value === "true",
                      label: option.label,
                    });
                  }}
                />
              );
            }}
          />
        </div>
        <div className={s.field}>
          <span className={s.label}>{t("employee.workPhone")}</span>
          <Controller
            name="workPhone"
            control={control}
            render={({ onChange, value, onBlur }) => (
              <Input
                value={value || ""}
                onChange={(event) => {
                  const val = event.target.value;
                  const digits = val.replace(/\D/g, "").slice(0, 10);
                  let formatted = "";
                  if (digits.length > 0) formatted = `+7(${digits.slice(0, 3)}`;
                  if (digits.length > 3) formatted += `)${digits.slice(3, 6)}`;
                  if (digits.length > 6) formatted += `-${digits.slice(6, 8)}`;
                  if (digits.length > 8) formatted += `-${digits.slice(8, 10)}`;
                  onChange(formatted);
                }}
                onBlur={onBlur}
                placeholder={t("partners.filter.inputPlaceholder")}
              />
            )}
          />
        </div>
      </div>

      <div className={s.row}>
        <div className={`${s.field} ${s.fieldNarrow}`}>
          <span className={s.label}>{t("users.id")}</span>
          <Controller
            name="id"
            control={control}
            render={({ onChange, value, onBlur }) => (
              <Input
                value={value || ""}
                onChange={(event) => onChange(event.target.value)}
                onBlur={onBlur}
                placeholder={t("partners.filter.inputPlaceholder")}
              />
            )}
          />
        </div>
        <div className={`${s.field} ${s.fieldNarrow}`}>
          <span className={s.label}>{t("employee.ladaAcademiaIds")}</span>
          <Controller
            name="ladaAcademiaId"
            control={control}
            render={({ onChange, value, onBlur }) => (
              <Input
                value={value || ""}
                onChange={(event) => onChange(event.target.value)}
                onBlur={onBlur}
                placeholder={t("partners.filter.inputPlaceholder")}
              />
            )}
          />
        </div>
        <div className={`${s.field} ${s.fieldNarrow}`}>
          <span className={s.label}>{t("employee.filterByLabel")}</span>
          <Controller
            name="filterBy"
            control={control}
            render={({ onChange, value }) => (
              <Select
                labelInValue
                value={
                  filterByOptions.find(
                    (item) => item.value === (value || ""),
                  ) || filterByOptions[0]
                }
                options={filterByOptions}
                onChange={(option) => {
                  setValue("filterValues", []);
                  onChange(option?.value || undefined);
                }}
              />
            )}
          />
        </div>
        <div className={`${s.field} ${s.fieldNarrow}`}>
          <span className={s.label}>{t("employee.filterValueLabel")}</span>
          <Controller
            name="filterValues"
            control={control}
            render={({ onChange, value }) => (
              <Select
                labelInValue
                mode="multiple"
                value={(value || []).map(
                  (item) =>
                    filterValueOptions.find(
                      (option) => option.value === item.value,
                    ) || item,
                )}
                loading={filterValueLoading}
                options={filterValueOptions as StatusOption[]}
                placeholder={t("employee.nothingSelected")}
                disabled={!filterByValue}
                onChange={(options) => onChange(options || [])}
                onPopupScroll={
                  isDealerFilter
                    ? (event) => {
                        const target = event.target as HTMLDivElement;
                        if (
                          target.scrollTop + target.offsetHeight >=
                          target.scrollHeight - 20
                        ) {
                          onScrollBottomDealers();
                        }
                      }
                    : undefined
                }
                onSearch={
                  isDealerFilter
                    ? searchDealer
                    : isPartnerFilter
                      ? searchPartner
                      : undefined
                }
                onOpenChange={
                  isPartnerFilter ? handlePartnersOpenChange : undefined
                }
                filterOption={isGeoFilter ? filterOptionByLabel : false}
                showSearch={Boolean(filterByValue)}
              />
            )}
          />
        </div>
        <div className={`${s.field} ${s.fieldNarrow}`}>
          <span className={s.label}>{t("employee.namePosition")}</span>
          <Controller
            name="positionIds"
            control={control}
            render={({ onChange, value }) => (
              <Select
                labelInValue
                mode="multiple"
                value={(value || []).map(
                  (item) =>
                    positionOptions.find(
                      (option) => option.value === item.value,
                    ) || item,
                )}
                loading={positionsLoading}
                options={positionOptions as StatusOption[]}
                placeholder={t("employee.noPosition")}
                onChange={(options) => onChange(options || [])}
                onSearch={searchPositions}
                onOpenChange={handlePositionsOpenChange}
                filterOption={false}
                showSearch
              />
            )}
          />
        </div>
        <div className={`${s.field} ${s.fieldRange}`}>
          <span className={s.label}>{t("employee.createAt")}</span>
          <div className={s.rangeWrapp}>
            <Controller
              control={control}
              name="creationFrom"
              render={({ onChange, value, onBlur }) => (
                <DatePicker
                  placeholder={t("employee.defaultDateFrom")}
                  format="DD.MM.YYYY"
                  value={value}
                  onBlur={onBlur}
                  onChange={(date) => onChange(date ?? null)}
                  disabledDate={DISABLED_FUTURE_DATE}
                />
              )}
            />
            <span className={s.rangeDivider}>-</span>
            <Controller
              control={control}
              name="creationTo"
              render={({ onChange, value, onBlur }) => (
                <DatePicker
                  placeholder={t("employee.defaultDateTo")}
                  format="DD.MM.YYYY"
                  value={value}
                  onBlur={onBlur}
                  onChange={(date) => onChange(date ?? null)}
                  disabledDate={DISABLED_FUTURE_DATE}
                />
              )}
            />
          </div>
        </div>
        <div className={`${s.field} ${s.fieldRange}`}>
          <span className={s.label}>{t("employee.dateOfChange")}</span>
          <div className={s.rangeWrapp}>
            <Controller
              control={control}
              name="lastEditFrom"
              render={({ onChange, value, onBlur }) => (
                <DatePicker
                  placeholder={t("employee.defaultDateFrom")}
                  format="DD.MM.YYYY"
                  value={value}
                  onBlur={onBlur}
                  onChange={(date) => onChange(date ?? null)}
                  disabledDate={DISABLED_FUTURE_DATE}
                />
              )}
            />
            <span className={s.rangeDivider}>-</span>
            <Controller
              control={control}
              name="lastEditTo"
              render={({ onChange, value, onBlur }) => (
                <DatePicker
                  placeholder={t("employee.defaultDateTo")}
                  format="DD.MM.YYYY"
                  value={value}
                  onBlur={onBlur}
                  onChange={(date) => onChange(date ?? null)}
                  disabledDate={DISABLED_FUTURE_DATE}
                />
              )}
            />
          </div>
        </div>
      </div>

      <div className={s.buttons}>
        <Button
          htmlType="button"
          color="secondary"
          variant="text"
          bold={false}
          fontSize="12"
          size="medium"
          onClick={cancelForm}
        >
          {t("employee.reset")}
        </Button>
        <Button
          htmlType="submit"
          bold
          color="secondary"
          fontSize="12"
          size="medium"
        >
          {t("employee.show")}
        </Button>
      </div>
    </form>
  );
};

export default BaseStaffFilter;
